import React, { useState, useRef, useEffect, useCallback } from "react";
import { FaChevronDown } from "react-icons/fa";
import { UsersFilter } from "../services/user.Service";
import { useGetAllOrganizationsQuery } from "../../organizations/organization.service";
import { useGetOrganizationProjectsQuery } from "../../organizations/organization.service";
import { useTheme } from "src/context/theme.context";

type SelectOption = {
  value: string;
  text: string;
};

export type SearchItemConfig = {
  type: "input" | "select";
  placeholder: string;
  options?: SelectOption[];
};

type Props = {
  updateQuery: (filter: UsersFilter) => void;
  initialQuery?: UsersFilter;
};

const searchItems: Record<string, SearchItemConfig> = {
  name: {
    type: "input",
    placeholder: "Enter user name",
  },
  email: {
    type: "input",
    placeholder: "Enter email address",
  },
  phone: {
    type: "input",
    placeholder: "Enter phone number",
  },
  userId: {
    type: "input",
    placeholder: "Enter user ID",
  },
  organization: {
    type: "select",
    placeholder: "Select organization",
    options: [],
  },
  project: {
    type: "select",
    placeholder: "Select project",
    options: [],
  },
};

export const UsersAutoSearch = ({ updateQuery, initialQuery = {} }: Props) => {
  const { isDarkMode } = useTheme();
  const [selected, setSelected] = useState<string>("name");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<UsersFilter>(initialQuery);
  const [showOnlyDisabled, setShowOnlyDisabled] = useState(Boolean(initialQuery.isSessionTemporarilyDisabled));
  const [isCheckboxDisabled, setIsCheckboxDisabled] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(initialQuery.organizationId || "");
  const [activeBadges, setActiveBadges] = useState<{ key: string; label: string; value: string }[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const previousSelectedRef = useRef<keyof typeof searchItems | null>(null);
  const currentFiltersRef = useRef<UsersFilter>(initialQuery);

  // Fetch organizations and projects
  const { data: organizations = [] } = useGetAllOrganizationsQuery();
  const { data: projects = [] } = useGetOrganizationProjectsQuery(selectedOrganizationId, {
    skip: !selectedOrganizationId,
  });

  // Update search items with dynamic options
  const updatedSearchItems: Record<string, SearchItemConfig> = {
    ...searchItems,
    organization: {
      ...searchItems.organization,
      options: organizations.map(org => ({ value: org.id, text: org.name })),
    },
    project: {
      ...searchItems.project,
      options: projects.map(project => ({ value: project.id, text: project.name })),
    },
  };

  const currentConfig = updatedSearchItems[selected];
  const items = Object.keys(updatedSearchItems);

  // Debounce function to avoid too many API calls
  const debounceRef = useRef<NodeJS.Timeout>();

  const buildActiveBadges = useCallback(
    (filters: UsersFilter) => {
      const badges: { key: string; label: string; value: string }[] = [];
      Object.entries(filters).forEach(([filterKey, filterValue]) => {
        if (filterKey === "isSessionTemporarilyDisabled") {
          if (filterValue) {
            badges.push({ key: filterKey, label: "session disabled", value: "true" });
          }
          return;
        }

        if (typeof filterValue !== "string" || !filterValue) return;

        let label = filterKey;
        let displayValue = filterValue;

        if (filterKey === "appProfile.phoneNumber") {
          label = "phone";
        } else if (filterKey === "organizationId") {
          label = "organization";
          const org = organizations.find((organization) => organization.id === filterValue);
          displayValue = org?.name || filterValue;
        } else if (filterKey === "projectId") {
          label = "project";
          const project = projects.find((projectItem) => projectItem.id === filterValue);
          displayValue = project?.name || filterValue;
        }

        badges.push({ key: filterKey, label, value: displayValue });
      });

      return badges;
    },
    [organizations, projects]
  );

  const applyFilters = useCallback(
    (nextFilters: UsersFilter, options?: { syncQuery?: boolean }) => {
      currentFiltersRef.current = nextFilters;
      setCurrentFilters(nextFilters);
      setActiveBadges(buildActiveBadges(nextFilters));

      if (options?.syncQuery === false) return;
      updateQuery(nextFilters);
    },
    [buildActiveBadges, updateQuery]
  );

  const handleSearch = useCallback(
    (key: string, value: string) => {
      const newFilters: UsersFilter = { ...currentFiltersRef.current };

      if (key === "userId") {
        if (value) {
          newFilters.userId = value;
        } else {
          delete newFilters.userId;
        }
      } else if (key === "name") {
        if (value) {
          newFilters.name = value;
        } else {
          delete newFilters.name;
        }
      } else if (key === "email") {
        if (value) {
          newFilters.email = value;
        } else {
          delete newFilters.email;
        }
      } else if (key === "phone") {
        if (value) {
          newFilters["appProfile.phoneNumber"] = value;
        } else {
          delete newFilters["appProfile.phoneNumber"];
        }
      } else if (key === "organization") {
        if (value) {
          newFilters.organizationId = value;
          setSelectedOrganizationId(value);
          delete newFilters.projectId;
          setQuery(value);
        } else {
          delete newFilters.organizationId;
          delete newFilters.projectId;
          setSelectedOrganizationId("");
          setQuery("");
        }
      } else if (key === "project") {
        if (value) {
          newFilters.projectId = value;
          setQuery(value);
        } else {
          delete newFilters.projectId;
          setQuery("");
        }
      }

      if (showOnlyDisabled) {
        newFilters.isSessionTemporarilyDisabled = true;
      } else {
        delete newFilters.isSessionTemporarilyDisabled;
      }

      applyFilters(newFilters);
    },
    [applyFilters, showOnlyDisabled]
  );

  // Handle checkbox change with 2-second cooldown
  const handleCheckboxChange = (checked: boolean) => {
    // Disable checkbox for 2 seconds to prevent rapid clicking
    setIsCheckboxDisabled(true);

    setShowOnlyDisabled(checked);
    const newFilters: UsersFilter = { ...currentFiltersRef.current };

    if (checked) {
      newFilters.isSessionTemporarilyDisabled = true;
    } else {
      delete newFilters.isSessionTemporarilyDisabled;
    }

    applyFilters(newFilters);

    // Re-enable checkbox after 2 seconds
    setTimeout(() => {
      setIsCheckboxDisabled(false);
    }, 2000);
  };

  const debounceSearch = useCallback(
    (key: string, value: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        handleSearch(key, value);
      }, 300); // 300ms debounce
    },
    [handleSearch]
  );

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    currentFiltersRef.current = initialQuery;
    setCurrentFilters(initialQuery);
    setShowOnlyDisabled(Boolean(initialQuery.isSessionTemporarilyDisabled));
    setSelectedOrganizationId(initialQuery.organizationId || "");
    setActiveBadges(buildActiveBadges(initialQuery));
  }, [buildActiveBadges, initialQuery]);

  // Reset query when switching between different search types
  useEffect(() => {
    // Only run if selected actually changed (not on initial render)
    if (previousSelectedRef.current !== null && previousSelectedRef.current !== selected) {
      setQuery("");
      // Clear the search for the new selected field
      handleSearch(selected, "");
    }
    // Update the ref for next time
    previousSelectedRef.current = selected;
  }, [selected, handleSearch]);

  // Handle input change with debounced search
  const handleInputChange = (value: string) => {
    setQuery(value);
    // For input fields, trigger debounced search
    if (currentConfig.type === "input") {
      debounceSearch(selected, value.trim());
    } else {
      // For select fields, search immediately
      handleSearch(selected, value);
    }
  };

  const renderInputField = () => {
    if (currentConfig.type === "select" && currentConfig.options) {
      const isProjectSelect = selected === "project";
      const isDisabled = isProjectSelect && !selectedOrganizationId;
      
      // Get the current selected value (ID)
      const getSelectedValue = () => {
        if (selected === "organization") {
          return selectedOrganizationId || "";
        } else if (selected === "project") {
          return currentFilters.projectId || "";
        }
        return query;
      };
      
      return (
        <select
          value={getSelectedValue()}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={isDisabled}
          className={`relative z-10 w-full px-6 py-4 bg-transparent text-lg border-none transition-colors duration-200 focus:outline-none ${
            isDarkMode ? "text-slate-100 focus:text-slate-100" : "text-slate-800 focus:text-emerald-700"
          } ${
            isDisabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <option value="">
            {isDisabled ? "Select organization first" : currentConfig.placeholder}
          </option>
          {currentConfig.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        id="search-dropdown"
        type="search"
        value={query}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={currentConfig.placeholder}
        className={`relative z-10 w-full px-6 py-4 bg-transparent focus:border-none focus:outline-none focus:ring-0 text-lg flex-grow border-none transition-colors duration-200 ${
          isDarkMode
            ? "text-slate-100 placeholder-slate-400 focus:text-slate-100"
            : "text-slate-800 placeholder-slate-500 focus:text-emerald-700"
        }`}
      />
    );
  };

  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <div className="w-full max-w-4xl mx-auto">
        <div ref={ref} className="relative group">
          <label htmlFor="search-dropdown" className="sr-only">
            Search category
          </label>

          {/* Main Search Container - No form wrapper since we don't need submit */}
          <div
            className={`
              flex rounded-2xl overflow-hidden transition-all duration-300 ease-out
              backdrop-blur-sm border-2 shadow-xl
              ${
                focused || open
                  ? `${isDarkMode ? "bg-slate-900/95 border-emerald-400 shadow-2xl shadow-emerald-950/50 ring-4 ring-emerald-500/15" : "bg-white/95 border-emerald-400 shadow-2xl shadow-emerald-200/60 ring-4 ring-emerald-100/50"} scale-[1.02]`
                  : `${isDarkMode ? "bg-slate-900/95 border-slate-700 hover:border-emerald-500/40 hover:shadow-2xl" : "bg-white/95 border-slate-200 hover:border-emerald-300 hover:shadow-2xl"} hover:scale-[1.01]`
              }
            `}
          >
            {/* Category Dropdown Button */}
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className={`
                  flex items-center px-6 py-4 bg-gradient-to-r transition-all duration-200
                  ${
                    open
                      ? "from-emerald-500 via-teal-500 to-cyan-500 text-white shadow-lg"
                      : isDarkMode
                      ? "from-slate-800 via-slate-850 to-slate-800 text-slate-100 hover:from-emerald-500/20 hover:via-teal-500/20 hover:to-cyan-500/20 hover:text-emerald-200"
                      : "from-slate-100 via-slate-50 to-emerald-50 text-slate-700 hover:from-emerald-100 hover:via-teal-100 hover:to-cyan-100 hover:text-emerald-800"
                  }
                  ${isDarkMode ? "border-r border-slate-700" : "border-r border-slate-200"} font-medium 
                `}
            >
              <span className="truncate capitalize">{selected}</span>
              <FaChevronDown
                className={`
                  ml-3 w-3 h-3 transition-transform duration-200
                  ${open ? "rotate-180" : ""}
                `}
              />
            </button>

            {/* Search Input/Select - No search button */}
            <div className="flex-1 relative">
              {renderInputField()}

              {/* Input glow effect */}
              {focused && (
                <div
                  className={`absolute inset-0 z-0 pointer-events-none border-none ${
                    isDarkMode
                      ? "bg-gradient-to-r from-emerald-500/5 via-teal-500/5 to-cyan-500/5"
                      : "bg-gradient-to-r from-emerald-50/30 via-teal-50/30 to-cyan-50/30"
                  }`}
                />
              )}
            </div>

            {/* Search indicator - shows when actively searching */}
            {query.trim() && (
              <div className="px-4 py-4 flex items-center justify-center text-emerald-500">
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
            )}
          </div>

          {/* Dropdown Menu */}
          <div
            className={`
              absolute top-full left-0 mt-2 z-50 transition-all duration-300 ease-out origin-top w-auto
              backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden
              ${isDarkMode ? "bg-slate-900/98 border border-slate-700" : "bg-white/98 border border-emerald-200"}
              ${open ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-2 pointer-events-none"}
            `}
          >
            <ul className="pl-0 list-none m-0 text-sm">
              {items.map((item, index) => (
                <li key={item}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(item);
                      setOpen(false);
                    }}
                    className={`
                        w-full px-20 py-3 text-left transition-all duration-150 flex items-center block
                        ${
                          selected === item
                            ? isDarkMode
                              ? "bg-gradient-to-r from-emerald-500/20 via-teal-500/20 to-cyan-500/20 text-emerald-200 font-medium border-r-4 border-emerald-400 shadow-sm"
                              : "bg-gradient-to-r from-emerald-100 via-teal-100 to-cyan-100 text-emerald-800 font-medium border-r-4 border-emerald-400 shadow-sm"
                            : isDarkMode
                            ? "text-slate-200 hover:bg-gradient-to-r hover:from-emerald-500/10 hover:via-teal-500/10 hover:to-cyan-500/10 hover:text-emerald-200"
                            : "text-slate-700 hover:bg-gradient-to-r hover:from-emerald-50 hover:via-teal-50 hover:to-cyan-50 hover:text-emerald-700"
                        }
                      `}
                    style={{
                      animationDelay: `${index * 50}ms`,
                    }}
                  >
                    <span className="flex-1 capitalize">{item}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Active filter badges */}
        {activeBadges.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeBadges.map((badge) => (
              <button
                key={`${badge.key}-${badge.value}`}
                onClick={() => {
                  if (badge.key === "isSessionTemporarilyDisabled") {
                    handleCheckboxChange(false);
                  } else {
                    // Map the filter key back to the search key
                    let searchKey = badge.key;
                    if (badge.key === "organizationId") searchKey = "organization";
                    if (badge.key === "projectId") searchKey = "project";
                    if (badge.key === "appProfile.phoneNumber") searchKey = "phone";
                    handleSearch(searchKey, "");
                  }
                }}
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs transition-colors duration-200 ${
                  isDarkMode
                    ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30 hover:bg-emerald-500/20"
                    : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                }`}
                title={`Remove ${badge.label}`}
              >
                <span className="font-medium capitalize">{badge.label}:</span>
                <span>{badge.value}</span>
                <span className="ml-1">×</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Session Disabled Filter Checkbox */}
      <div
        className={`flex items-center gap-3 p-4 backdrop-blur-sm rounded-xl shadow-lg ${
          isDarkMode ? "bg-slate-900/80 border border-slate-700" : "bg-white/80 border border-slate-200"
        }`}
      >
        <label
          className={`flex items-center gap-3 ${
            isCheckboxDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
          }`}
        >
          <input
            type="checkbox"
            checked={showOnlyDisabled}
            disabled={isCheckboxDisabled}
            onChange={(e) => handleCheckboxChange(e.target.checked)}
            className={`w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500 focus:ring-2 ${
              isDarkMode ? "bg-slate-800 border-slate-600" : "bg-gray-100 border-gray-300"
            } ${
              isCheckboxDisabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
          />
          <span
            className={`font-medium ${
              isCheckboxDisabled ? (isDarkMode ? "text-slate-500" : "text-slate-500") : isDarkMode ? "text-slate-100" : "text-slate-700"
            }`}
          >
            Show only users with disabled sessions
          </span>
        </label>
        {showOnlyDisabled && (
          <span
            className={`px-3 py-1 text-sm font-medium rounded-full ${
              isDarkMode ? "bg-emerald-500/15 text-emerald-200" : "bg-emerald-100 text-emerald-800"
            }`}
          >
            Filter Active
          </span>
        )}
        {isCheckboxDisabled && (
          <span
            className={`px-3 py-1 text-sm font-medium rounded-full animate-pulse ${
              isDarkMode ? "bg-yellow-500/15 text-yellow-200" : "bg-yellow-100 text-yellow-800"
            }`}
          >
            Cooldown (2s)
          </span>
        )}
      </div>
    </div>
  );
};
